/**
 * LifecycleSlider
 *
 * Date range slider for the Fire Lifecycle Visualizer.
 * Uses epoch ms values internally for the HTML range input.
 * Displays the selected date as a formatted label above the thumb.
 * Keyboard accessible — arrow keys advance/retreat by 1 day.
 */
import React, { useCallback } from 'react';

interface LifecycleSliderProps {
  startDate: string;
  endDate: string;
  selectedDate: string;
  onChange: (date: string) => void;
}

const DAY_MS = 86400000;

function toMs(dateStr: string): number {
  return new Date(dateStr).getTime();
}

function toDateStr(ms: number): string {
  return new Date(ms).toISOString().split('T')[0];
}

function formatDisplay(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

export const LifecycleSlider: React.FC<LifecycleSliderProps> = ({
  startDate,
  endDate,
  selectedDate,
  onChange,
}) => {
  const startMs = toMs(startDate);
  const endMs = toMs(endDate);
  const selectedMs = selectedDate ? toMs(selectedDate) : startMs;

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(toDateStr(Number(e.target.value)));
  }, [onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(selectedMs + DAY_MS, endMs);
      onChange(toDateStr(next));
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      const prev = Math.max(selectedMs - DAY_MS, startMs);
      onChange(toDateStr(prev));
    }
  }, [selectedMs, startMs, endMs, onChange]);

  return (
    <div className="lifecycle-slider">
      <div className="lifecycle-slider__label" aria-live="polite">
        {selectedDate ? formatDisplay(selectedDate) : '—'}
      </div>
      <input
        type="range"
        className="lifecycle-slider__input"
        min={startMs}
        max={endMs}
        step={DAY_MS}
        value={selectedMs}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        aria-label="Select date"
        aria-valuetext={selectedDate ? formatDisplay(selectedDate) : undefined}
      />
      <div className="lifecycle-slider__bounds">
        <span>{formatDisplay(startDate)}</span>
        <span>{formatDisplay(endDate)}</span>
      </div>
    </div>
  );
};
